
import angular from "angular";
const mLogSvc = new WeakMap();

import uiBootstrapModule from "angular-ui-bootstrap";
const mUibModalSvc = new WeakMap();

import EventWorkflowPreCheckServiceModule from "../../../services/EventWorkflowPreCheckService";
const mEventWorkflowPreCheckSvc = new WeakMap();

import UserInfoServiceModule from "../../../services/UserInfoService";
const mUserInfoSvc = new WeakMap();

import TrackingEventReportDataServiceModule from "../../../services/TrackingEventReportDataService";
const mTrackingEventReportDataSvc = new WeakMap();

import EncodedHtmlDecoderServiceModule from "../../../services/EncodedHtmlDecoderService";
const mEncodedHtmlDecoderSvc = new WeakMap();

import AllTrackingStatusesServiceModule from "../../../services/AllTrackingStatusesService";
const mAllTrackingStatusesSvc = new WeakMap();

import TrackingCalendarDataServiceModule from "../../../services/TrackingCalendarDataService";
const mTrackingCalendarDataSvc = new WeakMap();

const UserCancelledModalByClickingBackdropResultStatusMessage = "backdrop click";

import Assert from "tbAssert";
import DependencyListBuilder from "../../../utility/DependencyListBuilder";

import TrackingEvent from "../../../models/TrackingEvent";
import TrackingEventWorkflowTransition from "../../../models/TrackingEventWorkflowTransition";

import TrackingEventWorkflowCurrentStatus from "./TrackingEventWorkflowCurrentStatus";

import WorkflowError from "../../../models/errors/WorkflowError";
import WorkflowCancellationError from "../../../models/errors/WorkflowCancellationError";

import EventWorkflowWarningDialogController from "../event-workflow-warning-dialog/EventWorkflowWarningDialogController";
import EventReportDialogController from "../EventReportDialogController";

class TrackingEventWorkflowService {
    constructor($log, $uibModal, EventWorkflowPreCheckService, UserInfoService, TrackingEventReportData, EncodedHtmlDecoder, AllTrackingStatuses, TrackingCalendarData) {
        Assert.StrictAssert($log != null, "Event Workflow Service missing or invalid log service dependency");
        Assert.StrictAssert($uibModal != null, "Event Workflow Service missing or invalid angular ui bootstrap modal service dependency");
        Assert.StrictAssert(EventWorkflowPreCheckService != null, "Event Workflow Service missing or invalid tracking event workflow pre-check service dependency");
        Assert.StrictAssert(UserInfoService != null, "Event Workflow Service missing or invalid user info service dependency");
        Assert.StrictAssert(TrackingEventReportData != null, "Event Workflow Service missing or invalid tracking event report data service dependency");
        Assert.StrictAssert(EncodedHtmlDecoder != null, "Event Workflow Service missing or invalid encoded html decoder service dependency");
        Assert.StrictAssert(AllTrackingStatuses != null, "Event Workflow Service missing or invalid all tracking statuses service dependency");
        Assert.StrictAssert(TrackingCalendarData != null, "Event Workflow Service missing or invalid tracking calendar data service dependency");
        
        mLogSvc.set(this, $log);
        mUibModalSvc.set(this, $uibModal);
        mEventWorkflowPreCheckSvc.set(this, EventWorkflowPreCheckService);
        mUserInfoSvc.set(this, UserInfoService);
        mTrackingEventReportDataSvc.set(this, TrackingEventReportData);
        mEncodedHtmlDecoderSvc.set(this, EncodedHtmlDecoder);
        mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
    }

    //Service entry point
    runTransitionWorkflow({
        targetEvent,
        selectedTransition,
        cachedTrackingEventReport,
        dialogOptions // { dialogBackDropClass, dialogWindowClass, dialogSize }
    }) {
        if ((targetEvent instanceof TrackingEvent) !== true) {
            throw new TypeError("Workflow Transition Error: target event parameter is not a TrackingEvent");
        }

        if ((selectedTransition instanceof TrackingEventWorkflowTransition) !== true) {
            throw new TypeError("Workflow Transition Error: selected transition parameter is not a TrackingEventWorkflowTransition");
        }

        let transitionPipeline = [];
        if (selectedTransition.preCheckAvailable === true) {
            transitionPipeline.push(this._addWorkflowPreCheckCallPreCheckServiceStep({
                targetEvent: targetEvent,
                selectedTransition: selectedTransition
            }));
            transitionPipeline.push(this._addWorkflowPreCheckShowDialog(dialogOptions));
            transitionPipeline.push(this._addPreCheckWorkflowDialogResultErrorHandler());
        }

        if (selectedTransition.isReportRequired) {
            this._addEventReportingWorkflowToTransitionPipeline({
                workflowPipeline: transitionPipeline,
                targetEvent: targetEvent,
                targetTransition: selectedTransition,
                cachedTrackingReport: cachedTrackingEventReport,
                reportDialogParams: dialogOptions});
        }

        transitionPipeline.push(this._addWorkflowTransitionExecutionStep({targetTransition: selectedTransition}));

        return this._runWorkflowPipeline({pipeline: transitionPipeline});
    }

    doTrackingReportOutOfBandEdit({
        targetEvent,
        trackingEventReport,
        dialogOptions
    }) {
        if (!(targetEvent instanceof TrackingEvent)) {
            throw new TypeError("Tracking Report Edit: invalid tracking event parameter");
        }

        let workflowPipeline = [];
        workflowPipeline.push(this._addGetTrackingReportStep({
            trackingEventId: targetEvent.EventId,
            cachedTrackingEventReport: trackingEventReport
        }));

        workflowPipeline.push(this._addOpenEventReportDialogStep({
            targetEvent: targetEvent,
            targetStatusName: targetEvent.trackingStatus.trackingStatusName,
            dialogParams: dialogOptions
        }));

        workflowPipeline.push(this._addEventReportDialogResultErrorHandler());
        workflowPipeline.push(this._addUpdateTrackingReportStep({targetEvent: targetEvent}));

        return this._runWorkflowPipeline({pipeline: workflowPipeline});
    }

    getTrackingReport({
        targetEvent,
        cachedTrackingEventReport
    }) {

        let workflowPipeline = [];

        workflowPipeline.push(this._addGetTrackingReportStep({
            trackingEventId: targetEvent.EventId,
            cachedTrackingEventReport: cachedTrackingEventReport
        }));
        workflowPipeline.push(this._addPackageTrackingReportDataStep());

        return this._runWorkflowPipeline({pipeline: workflowPipeline});
    }

    _runWorkflowPipeline({pipeline: workflowPipeline}) {
        let workflowPromise = Promise.resolve(new TrackingEventWorkflowCurrentStatus({status: "OK.PipelineStart"}));
        workflowPipeline.forEach((pipelineStepFactory) => {
            workflowPromise = pipelineStepFactory(workflowPromise);
        });

        return workflowPromise;
    }

    _addWorkflowPreCheckCallPreCheckServiceStep({targetEvent, selectedTransition}) {
        return (selectedTransition.isReportRequired) ?
            (transitionPromise) => transitionPromise.then(() => mEventWorkflowPreCheckSvc.get(this).preCheckReportEvent(targetEvent)) :
            (transitionPromise) => transitionPromise.then(() => mEventWorkflowPreCheckSvc.get(this).preCheckScheduleEvent(targetEvent));
    }

    _addWorkflowPreCheckShowDialog({
        dialogBackDropClass,
        dialogWindowClass,
        dialogSize
    }) {
        let uibModalSvc = mUibModalSvc.get(this);

        let eventWorkflowWarningDialogParams = {
            animation: true,
            template: EventWorkflowWarningDialogController.template,
            controller: EventWorkflowWarningDialogController,
            controllerAs: EventWorkflowWarningDialogController.controllerAs,
            bindToController: true,
            backdropClass: dialogBackDropClass,
            windowClass: dialogWindowClass,
            size: dialogSize,
            resolve: {}
        };

        return (transitionPromise) =>
            transitionPromise
                .then((preCheckResult) => {
                    if (!preCheckResult.preCheckPassed) {
                        eventWorkflowWarningDialogParams.resolve["preCheckResult"] = () => preCheckResult;
                        return uibModalSvc.open(eventWorkflowWarningDialogParams).result;
                    }
                    else {
                        return new TrackingEventWorkflowCurrentStatus({status: "OK.PreCheckPassedNoIssues"});
                    }
                })
                .then((dialogStepResult) => {
                    if (dialogStepResult instanceof TrackingEventWorkflowCurrentStatus) {
                        return dialogStepResult;
                    }
                    else if (dialogStepResult === EventWorkflowWarningDialogController.UserOverrideStatusMessage) {
                        return new TrackingEventWorkflowCurrentStatus({status: "OK.PreCheckUserOverride"});
                    }
                    else {
                        throw new WorkflowError({
                            stepName: "PreCheckWorkflowDialogResultHandler",
                            message: "Unrecognized result from pre-check dialog"
                        });
                    }
                });
    }

    _addPreCheckWorkflowDialogResultErrorHandler() {
        return (transitionPromise) =>
            transitionPromise.catch((reason) => {
                if (reason instanceof Error) {
                    throw reason;
                }
                else if (reason === EventWorkflowWarningDialogController.UserCancelledStatusMessage) {
                    throw new WorkflowCancellationError("User cancelled after pre-check");
                }
                else if (reason === UserCancelledModalByClickingBackdropResultStatusMessage) {
                    throw new WorkflowCancellationError("User cancelled after pre-check by clicking modal dialog backdrop");
                }
                else {
                    throw new WorkflowError({ stepName: "PreCheckWorkflowDialog", message: reason });
                }
            });
    }

    _addEventReportingWorkflowToTransitionPipeline({
        workflowPipeline,
        targetEvent,
        targetTransition,
        cachedTrackingReport,
        reportDialogParams
    }) {

        workflowPipeline.push(this._addGetTrackingReportStep({
            trackingEventId: targetEvent.EventId,
            cachedTrackingEventReport: cachedTrackingReport
        }));

        workflowPipeline.push(this._addOpenEventReportDialogStep({
            targetEvent: targetEvent,
            targetStatusName: targetTransition.friendlyName,
            dialogParams: reportDialogParams
        }));

        workflowPipeline.push(this._addEventReportDialogTransitionWorkflowResultHandler());
        workflowPipeline.push(this._addEventReportDialogResultErrorHandler());
    }

    _addOpenEventReportDialogStep({targetEvent, targetStatusName, dialogParams: {
        dialogBackDropClass,
        dialogWindowClass,
        dialogSize
    }}) {
        const uibModalSvc = mUibModalSvc.get(this);
        const userInfoSvc = mUserInfoSvc.get(this);
        const proposedTrackingStatus = mAllTrackingStatusesSvc.get(this).getProposedStatus();
        let reportResult = targetStatusName;

        return (transitionPromise) => transitionPromise.then((currentTrackingReportData) => {

            let reportingModalInstance = uibModalSvc.open({
                animation: true,
                template: EventReportDialogController.template,
                controller: EventReportDialogController,
                controllerAs: EventReportDialogController.controllerAs,
                bindToController: true,
                backdropClass: dialogBackDropClass,
                windowClass: dialogWindowClass,
                size: dialogSize,
                resolve: {
                    eventInfo: () => {
                        return {
                            eventName: targetEvent.getEventDisplayName(),
                            eventDates: {
                                start: new Date(targetEvent.startDate.getTime()),
                                end: new Date(targetEvent.endDate.getTime())
                            },
                            eventAttendees: targetEvent.eventAttendees.map(
                                (attendee) => {
                                    return {
                                        firstName: attendee.firstName,
                                        lastName: attendee.lastName
                                    };
                                })
                        };
                    },
                    statusEditMode: () => {
                        let trackingStatus = targetEvent.trackingStatus;
                        let isPreFileState = (trackingStatus.trackingStatusName === proposedTrackingStatus.trackingStatusName);
                        let isPostFileState = (trackingStatus.isReported === true);
                        let isFileState = (trackingStatus.isApproved === true);

                        const eventReportDialogModes = EventReportDialogController.ReportDialogModes;

                        let selectedEventReportDialogMode = null;
                        if (isPreFileState) {
                            selectedEventReportDialogMode = eventReportDialogModes.PreFile;
                        }
                        else if (isPostFileState) {
                            selectedEventReportDialogMode = eventReportDialogModes.PostFile;
                        }
                        else if (isFileState) {
                            selectedEventReportDialogMode = eventReportDialogModes.File;
                        }
                        else {
                            selectedEventReportDialogMode = eventReportDialogModes.ViewOnly;
                        }

                        const canNotifySubscribers = (userInfoSvc.hasManagementRights === true);

                        return {
                            mode: selectedEventReportDialogMode,
                            canNotifySubscribers: canNotifySubscribers
                        };
                    },
                    selectedResult: () => reportResult,
                    currentTrackingReport: () => currentTrackingReportData
                }
            });

            return reportingModalInstance.result;
        });
    }

    _addEventReportDialogTransitionWorkflowResultHandler() {
        return (transitionPromise) => transitionPromise
            .then((eventReportDialogResult) => {
                let updatedReportText = eventReportDialogResult.updatedTrackingReport;

                if (updatedReportText == null) {
                    throw new WorkflowError({
                        stepName: "EventReportDialogTransitionWorkflowResultHandler",
                        message: "The updated report text was null or undefined"
                    });
                }

                return new TrackingEventWorkflowCurrentStatus({
                    status: "OK.GotReportText",
                    data: { reportText: updatedReportText }
                });
            });
    }

    _addEventReportDialogResultErrorHandler() {

        return (workflowPromise) => workflowPromise
            .catch((reason) => {
                if (reason instanceof Error) {
                    throw reason;
                }
                else if (reason === EventReportDialogController.ReportDialogUserCancelledStatus) {
                    throw new WorkflowCancellationError("User cancelled out of event report dialog");
                }
                else if (reason === UserCancelledModalByClickingBackdropResultStatusMessage) {
                    throw new WorkflowCancellationError("User cancelled out of event report dialog by clicking dialog backdrop");
                }
                else {
                    throw new WorkflowError({
                        stepName: "EventReportDialogResultErrorHandler",
                        message: reason
                    });
                }
            });
    }

    _addUpdateTrackingReportStep({targetEvent}) {
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);

        return (workflowPromise) => workflowPromise
            .then((reportDialogResult) =>
            trackingCalendarDataSvc.updateTrackingReport(
                targetEvent.trackingEventId,
                targetEvent.trackingStatus,
                reportDialogResult.updatedTrackingReport,
                reportDialogResult.updatedPreFileStatus,
                reportDialogResult.notifySubscribers
            ))
            .then((updatedEvent) => {
                if (updatedEvent instanceof TrackingEvent) {
                    return new TrackingEventWorkflowCurrentStatus({
                        status: "OK.EventReportUpdated",
                        data: {
                            updatedEvent: updatedEvent
                        }
                    });
                }
                else {
                    throw new WorkflowError({
                        stepName: "UpdateTrackingReportResultHandler",
                        message: "Updated event data null or invalid."
                    });
                }
            })
            .catch((reason) => {
                if (reason instanceof Error) {
                    throw reason;
                }
                else {
                    throw new WorkflowError({
                        stepName: "UpdateTrackingReportErrorHandler",
                        message: reason
                    });
                }
            });
    }

    _addGetTrackingReportStep({trackingEventId, cachedTrackingEventReport}) {

        let trackingEventReportDataSvc = mTrackingEventReportDataSvc.get(this);
        let encodedHtmlDecoderSvc = mEncodedHtmlDecoderSvc.get(this);

        return (currentPromise) => currentPromise
            .then(() => trackingEventReportDataSvc.get({
                eventId: trackingEventId
            }).$promise)
            .then((reportResult) => {
                cachedTrackingEventReport.downloaded = true;
                cachedTrackingEventReport.reportData = reportResult.result;
                cachedTrackingEventReport.reportData.decodedReport =
                    encodedHtmlDecoderSvc.decode(cachedTrackingEventReport.reportData.report);
                cachedTrackingEventReport.decodedReport =
                    angular.copy(cachedTrackingEventReport.reportData.decodedReport);

                return cachedTrackingEventReport.reportData;
            })
            .catch((reason) => {
                if (reason instanceof Error) { throw reason; }
                else {
                    throw new WorkflowError({
                        stepName: "GetTrackingReport",
                        message: "Tracking Event Report Data Retrieval Failed"
                    });
                }
            });
    }

    _addPackageTrackingReportDataStep() {
        return (workflowPromise) => workflowPromise
            .then((reportData) => {
                return new TrackingEventWorkflowCurrentStatus({
                    status: "OK.RetrievedReportData",
                    data: { updatedReportData: reportData }
                });
            })
            .catch((reason) => {
                if (reason instanceof Error) { throw reason; }
                else {
                    throw new WorkflowError({stepName: "PackageTrackingReport", message: reason });
                }
            });
    }

    _addWorkflowTransitionExecutionStep({targetTransition}) {
        const stepBaseName = "WorkflowTransitionExecute";
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);

        return (transitionPromise) => transitionPromise
            .then((currentWorkflowStatus) => {
                if (currentWorkflowStatus.workflowStatus.slice(0, 2) !== "OK") {
                    throw new WorkflowError({
                        stepName: `Pre${stepBaseName}`,
                        message: "current workflow status doesn't start with OK."
                    });
                }

                let reportText = currentWorkflowStatus.currentReportText;

                return trackingCalendarDataSvc.doWorkflowTransition(
                    targetTransition,
                    reportText
                );
            })
            .then((updatedEvent) => new TrackingEventWorkflowCurrentStatus({
                status: "OK.WorkflowCompleted",
                data: {
                    updatedEvent: updatedEvent
                }
            }))
            .catch((reason) => {
                if (reason instanceof Error) {
                    throw reason;
                }
                else {
                    throw new WorkflowError({
                        stepName: stepBaseName,
                        message: reason
                    });
                }
            });
    }

}

TrackingEventWorkflowService.$inject = [
    "$log",
    "$uibModal",
    "EventWorkflowPreCheckService",
    "UserInfoService",
    "TrackingEventReportData",
    "EncodedHtmlDecoder",
    "AllTrackingStatuses",
    "TrackingCalendarData"
];

const trackingEventWorkflowServiceDependencies = DependencyListBuilder([
    EventReportDialogController.dependencies,
    EventWorkflowWarningDialogController.dependencies,
    [
        uiBootstrapModule,
        EventWorkflowPreCheckServiceModule,
        UserInfoServiceModule,
        TrackingEventReportDataServiceModule,
        EncodedHtmlDecoderServiceModule,
        AllTrackingStatusesServiceModule,
        TrackingCalendarDataServiceModule
    ]
]);

export default angular
    .module(
        "trackerbase-app.views.tracking-event-editor.tracking-event-workflow-service",
        trackingEventWorkflowServiceDependencies
    )
    .service("TrackingEventWorkflowService", TrackingEventWorkflowService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-event-editor/event-transition/TrackingEventWorkflowService.js
// module id = k89K
// module chunks = 1